import { useCallback } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { RoundingRuleMutationPayload } from 'repository/timekeeper/@types/RoundingRules';
import { SnackbarMessage } from 'notistack';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const useRoundingRulesActions = () => {
    const addRoundingRule = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: RoundingRuleMutationPayload;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);
            console.log('Adding Rounding Rule with body:', body);

            try {
                await timekeeperService.rounding_rules.createRoundingRule(body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateRoundingRule = useCallback(
        async ({
            setLoading,
            roundingRuleId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            roundingRuleId: number;
            body: RoundingRuleMutationPayload;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.rounding_rules.updateRoundingRule(roundingRuleId, body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteRoundingRule = useCallback(
        async ({
            setLoading,
            roundingRuleId,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            roundingRuleId: number;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.rounding_rules.deleteRoundingRule(roundingRuleId);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return { addRoundingRule, updateRoundingRule, deleteRoundingRule };
};
